/**
 * WH-RESTOCK-02 (Seth 2026-09-29): cycle counts per person — techs,
 * installers, plumbers, new construction — with Pegasus doing the
 * remembering the old per-tech Drive workbooks depended on humans for.
 *
 * v3 (Seth: "build this out for pegasus and not so much ESC messiness…
 * Install/Sales should just be Install. Sales would be Larry… dummy
 * accounts… office employees wouldn't ever need to be added… does it save
 * cycle counts?"): the people picker now reads the PEGASUS roster
 * (tech_profile — the Team page's truth), so the groups are Service /
 * Install / Plumbing / New Construction / Sales with only real people;
 * ESC's dummy dispatch accounts never appear and Office sits behind one
 * small link. People are durable rows (pegasus_count_owner) with their own
 * cadence (weekly / every 2 weeks / monthly), so the setup carries season
 * to season with nothing to rebuild — and every saved count lives forever
 * in pegasus_stock_count. The confusing copy-dropdown became plain choices:
 * Pick items now · Copy someone's list · Copy the shelf list.
 *
 * Shelf counts (Warehouse chip) update item.qty_in_stock. A person's counts
 * live on their own list rows — they never touch shelf stock.
 */
import { useMemo, useState } from "react";
import { ClipboardCheck, Check, X, Plus, Loader2, Trash2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { InfoTip } from "@/components/InfoTip";
import { PricebookPicker } from "@/features/items/PricebookPicker";
import { CycleCountCard } from "./CycleCountCard";
import {
  useCountLists, useAddCountItem, useRemoveCountItem, useSaveTruckCounts, useSeedCountRows,
  useCountOwners, useAddOwners, useUpdateOwner, useRemoveOwner, useCountRoster, countDue,
  type CountListRow, type CountOwner, type CountRosterPerson,
} from "./restock";

const qtyText = (n: number) => (Number.isInteger(Number(n)) ? String(Number(n)) : Number(n).toFixed(2).replace(/0+$/, "").replace(/\.$/, ""));
const dayText = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const firstName = (label: string) => label.split(/[,\s]+/)[0] || label;

const GROUP_ORDER = ["Service", "Install", "Plumbing", "New Construction", "Sales", "Warehouse", "Unassigned"];
const INTERVALS: { days: number; label: string }[] = [
  { days: 7, label: "Weekly" },
  { days: 14, label: "Every 2 Weeks" },
  { days: 30, label: "Monthly" },
];

/** One person's list: enter counts, add/remove items, cadence, remove person. */
function TruckPanel({ owner, rows, onRemoved }: { owner: CountOwner; rows: CountListRow[]; onRemoved: () => void }) {
  const save = useSaveTruckCounts();
  const addItem = useAddCountItem();
  const remove = useRemoveCountItem();
  const updateOwner = useUpdateOwner();
  const removeOwner = useRemoveOwner();
  const [vals, setVals] = useState<Record<string, string>>({});
  const [picking, setPicking] = useState(false);
  const [arm, setArm] = useState<string | null>(null);
  const [armGone, setArmGone] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const empno = owner.owner_empno;
  const label = owner.owner_label;

  const parsed = (r: CountListRow): number | null => {
    const raw = (vals[r.id] ?? "").trim();
    if (raw === "") return null;
    const n = Number(raw);
    return Number.isNaN(n) || n < 0 ? null : n;
  };
  const filled = rows.filter((r) => parsed(r) != null);

  const saveAll = async () => {
    const batch = filled.map((r) => ({ row: r, counted: parsed(r) as number }));
    if (batch.length === 0) return;
    await save.mutateAsync(batch);
    setVals({});
    setArm(null);
    setFlash(`Saved ${batch.length}.`);
    window.setTimeout(() => setFlash(null), 4000);
  };

  const intervalOptions = INTERVALS.some((i) => i.days === owner.interval_days)
    ? INTERVALS
    : [...INTERVALS, { days: owner.interval_days, label: `Every ${owner.interval_days} days` }];

  return (
    <div>
      {/* cadence + remove — quiet utility row */}
      <div className="mt-2.5 flex items-center gap-2">
        <select
          value={owner.interval_days}
          onChange={(e) => updateOwner.mutate({ empno, interval_days: Number(e.target.value) })}
          title="How often these come due"
          className="rounded-lg border border-hairline bg-white px-2 py-1 text-[12.5px] font-semibold text-slate-600">
          {intervalOptions.map((i) => <option key={i.days} value={i.days}>{i.label}</option>)}
        </select>
        <button type="button"
          onClick={() => {
            if (armGone) { removeOwner.mutate(empno, { onSuccess: onRemoved }); setArmGone(false); }
            else { setArmGone(true); window.setTimeout(() => setArmGone(false), 3000); }
          }}
          className={cn(
            "ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12.5px] font-bold",
            armGone ? "bg-rose-600 text-white" : "text-slate-300 hover:bg-slate-100 hover:text-slate-500",
          )}>
          <Trash2 className="h-3.5 w-3.5" />
          {armGone ? `Remove ${label}?` : ""}
        </button>
      </div>

      {flash && (
        <p className="mt-2 flex items-center gap-1.5 rounded-xl bg-emerald-50 px-3 py-2 text-[13.5px] font-bold text-emerald-800">
          <Check className="h-4 w-4" strokeWidth={3} /> {flash}
        </p>
      )}

      {rows.length === 0 ? (
        <div className="mt-2 rounded-xl border border-dashed border-hairline bg-slate-50/60 px-4 py-4 text-center">
          <p className="text-[13px] text-slate-400">Nothing on {label}'s list yet.</p>
          {!picking && (
            <button type="button" onClick={() => setPicking(true)}
              className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2.5 text-[14px] font-bold text-white shadow-sm hover:bg-slate-800">
              <Plus className="h-4 w-4" strokeWidth={3} /> Add Items
            </button>
          )}
        </div>
      ) : (
        <ul className="mt-2 space-y-2">
          {rows.map((r) => {
            const bad = (vals[r.id] ?? "").trim() !== "" && parsed(r) == null;
            const due = countDue(r, owner.interval_days);
            return (
              <li key={r.id} className={cn("flex items-center gap-3 rounded-2xl border p-3", due ? "border-hairline bg-white" : "border-emerald-100 bg-emerald-50/40")}>
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-semibold leading-snug text-slate-900">{r.description ?? r.part_number}</p>
                  <p className="mt-0.5 text-[12.5px] text-slate-500">
                    {r.part_number}
                    {" · "}
                    {r.last_counted_at
                      ? <>had {r.last_qty != null ? qtyText(Number(r.last_qty)) : "?"} · {dayText(r.last_counted_at)}</>
                      : <span className="font-semibold text-amber-700">first count</span>}
                  </p>
                </div>
                <input
                  value={vals[r.id] ?? ""}
                  onChange={(e) => setVals((v) => ({ ...v, [r.id]: e.target.value }))}
                  inputMode="decimal"
                  placeholder="#"
                  aria-label={`Count for ${r.part_number}`}
                  className={cn(
                    "w-20 shrink-0 rounded-xl border py-2.5 text-center text-[17px] font-extrabold tabular-nums focus:outline-none focus:ring-2",
                    bad ? "border-rose-300 focus:ring-rose-400/40" : "border-hairline focus:border-brand-500 focus:ring-brand-500/30",
                  )}
                />
                <button type="button"
                  title={arm === r.id ? "Tap again to remove" : "Remove from this list"}
                  onClick={() => {
                    if (arm === r.id) { remove.mutate(r.id); setArm(null); }
                    else setArm(r.id);
                  }}
                  className={cn(
                    "grid h-8 shrink-0 place-items-center rounded-lg text-[12px] font-bold",
                    arm === r.id ? "w-auto bg-rose-600 px-2 text-white" : "w-8 text-slate-300 hover:bg-slate-100 hover:text-slate-500",
                  )}>
                  {arm === r.id ? "Sure?" : <X className="h-4 w-4" strokeWidth={2.5} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {rows.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-hairline pt-3">
          <button type="button" disabled={filled.length === 0 || save.isPending} onClick={() => void saveAll()}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-[14px] font-bold text-white shadow-sm hover:bg-slate-800 disabled:opacity-40">
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {filled.length > 0 ? `Save ${filled.length}` : "Save"}
          </button>
          {!picking && (
            <button type="button" onClick={() => setPicking(true)}
              className="ml-auto inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[13px] font-bold text-brand-700 hover:bg-brand-50">
              <Plus className="h-3.5 w-3.5" strokeWidth={3} /> Add Items
            </button>
          )}
        </div>
      )}

      {picking && (
        <div className="mt-3 rounded-xl border border-hairline bg-slate-50 p-3">
          <PricebookPicker
            placeholder={`Add to ${label}'s list…`}
            pickLabel="Add"
            autoFocus
            onPick={(it) => addItem.mutate({ owner_empno: empno, owner_label: label, item: it })}
          />
          <button type="button" onClick={() => setPicking(false)}
            className="mt-2 rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-slate-500 hover:bg-slate-100">
            Done
          </button>
        </div>
      )}
    </div>
  );
}

/** + People — the PEGASUS roster, grouped, with plain starter-item choices. */
function AddPeoplePanel({
  owners, lists, onClose, onSeeded, onBulk,
}: {
  owners: CountOwner[];
  lists: CountListRow[];
  onClose: () => void;
  onSeeded: (firstEmpno: string) => void;
  onBulk: (people: { empno: string; label: string }[]) => void;
}) {
  const { data: roster = [], isLoading } = useCountRoster();
  const addOwners = useAddOwners();
  const seed = useSeedCountRows();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [start, setStart] = useState<string>("__PICK__"); // __PICK__ | __SHELF__ | <owner empno>
  const [showOffice, setShowOffice] = useState(false);
  const [busy, setBusy] = useState(false);

  const already = useMemo(() => new Set(owners.map((o) => o.owner_empno)), [owners]);
  const groups = useMemo(() => {
    const by = new Map<string, CountRosterPerson[]>();
    for (const p of roster) {
      if (already.has(p.empno)) continue;           // on counts already
      if (p.group === "Office" && !showOffice) continue;
      const arr = by.get(p.group) ?? [];
      arr.push(p);
      by.set(p.group, arr);
    }
    const order = (g: string) => {
      const i = GROUP_ORDER.indexOf(g);
      return i === -1 ? (g === "Office" ? 98 : 50) : i;
    };
    return [...by.entries()]
      .map(([group, members]) => ({ group, members }))
      .sort((a, b) => order(a.group) - order(b.group) || a.group.localeCompare(b.group));
  }, [roster, already, showOffice]);

  const toggleMany = (es: string[]) => setPicked((p) => {
    const n = new Set(p);
    const allIn = es.length > 0 && es.every((e) => n.has(e));
    for (const e of es) { if (allIn) n.delete(e); else n.add(e); }
    return n;
  });
  const allPicked = (es: string[]) => es.length > 0 && es.every((e) => picked.has(e));

  const people = useMemo(() => {
    const out: { empno: string; label: string }[] = [];
    for (const g of groups) for (const m of g.members) {
      if (picked.has(m.empno)) out.push({ empno: m.empno, label: firstName(m.name) });
    }
    return out;
  }, [groups, picked]);

  const add = async () => {
    if (people.length === 0) return;
    setBusy(true);
    try {
      await addOwners.mutateAsync(people); // people persist from this moment
      if (start === "__PICK__") { onBulk(people); return; }
      let src: { item_id: string; part_number: string | null; description: string | null }[] = [];
      if (start === "__SHELF__") {
        const { data } = await supabase.from("item").select("id, part_number, description")
          .eq("count_tracked", true).eq("inactive", false).eq("archived", false).limit(500);
        src = ((data ?? []) as { id: string; part_number: string; description: string | null }[])
          .map((i) => ({ item_id: i.id, part_number: i.part_number, description: i.description ?? i.part_number }));
      } else {
        src = lists.filter((r) => r.owner_empno === start)
          .map((r) => ({ item_id: r.item_id, part_number: r.part_number, description: r.description }));
      }
      if (src.length === 0) { onBulk(people); return; } // nothing to copy — pick together instead
      await seed.mutateAsync(people.flatMap((p) => src.map((s) => ({ owner_empno: p.empno, owner_label: p.label, ...s }))));
      onSeeded(people[0].empno);
    } finally {
      setBusy(false);
    }
  };

  const StartChip = ({ value, label }: { value: string; label: string }) => (
    <button type="button" onClick={() => setStart(value)}
      className={cn(
        "rounded-full px-3 py-1.5 text-[12.5px] font-bold",
        start === value ? "bg-brand-600 text-white" : "bg-white text-slate-600 ring-1 ring-hairline hover:bg-slate-100",
      )}>
      {label}
    </button>
  );

  return (
    <div className="mt-3 rounded-xl border border-hairline bg-slate-50 p-3.5">
      {isLoading ? (
        <p className="py-4 text-center text-[13px] text-slate-400">Loading the roster…</p>
      ) : (
        <>
          {groups.map((g) => (
            <div key={g.group} className="mb-2.5">
              <div className="mb-1.5 flex items-center gap-2">
                <span className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-slate-400">{g.group}</span>
                <button type="button" onClick={() => toggleMany(g.members.map((m) => m.empno))}
                  className={cn(
                    "rounded-md px-1.5 py-0.5 text-[11px] font-bold",
                    allPicked(g.members.map((m) => m.empno)) ? "bg-slate-900 text-white" : "text-brand-700 hover:bg-brand-50",
                  )}>
                  All
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {g.members.map((m) => (
                  <button key={m.empno} type="button" onClick={() => toggleMany([m.empno])}
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[13px] font-semibold",
                      picked.has(m.empno) ? "bg-slate-900 text-white" : "bg-white text-slate-700 ring-1 ring-hairline hover:bg-slate-100",
                    )}>
                    {picked.has(m.empno) && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                    {m.name}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {groups.length === 0 && (
            <p className="py-2 text-center text-[13px] text-slate-400">Everyone's already on counts.</p>
          )}
          {!showOffice && (
            <button type="button" onClick={() => setShowOffice(true)}
              className="text-[12px] font-semibold text-slate-400 underline-offset-2 hover:text-slate-600 hover:underline">
              Show Office
            </button>
          )}

          <div className="mt-3 border-t border-hairline pt-2.5">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-slate-400">Start with</span>
              <StartChip value="__PICK__" label="Pick items now" />
              {owners.map((o) => (
                <StartChip key={o.owner_empno} value={o.owner_empno} label={`Copy ${o.owner_label}'s list`} />
              ))}
              <StartChip value="__SHELF__" label="Copy the shelf list" />
            </div>
            <div className="mt-2.5 flex items-center gap-2">
              <button type="button" disabled={people.length === 0 || busy} onClick={() => void add()}
                className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2 text-[13.5px] font-bold text-white shadow-sm hover:bg-slate-800 disabled:opacity-40">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {people.length === 0 ? "Add People" : people.length === 1 ? `Add ${people[0].label}` : `Add ${people.length} People`}
              </button>
              <button type="button" onClick={onClose}
                className="rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-slate-500 hover:bg-slate-100">
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** The Cycle Counts card on /warehouse: Warehouse chip + one chip per person. */
export function CountsHub() {
  const { data: lists } = useCountLists();
  const { data: ownerRows } = useCountOwners();
  const seed = useSeedCountRows();
  const [owner, setOwner] = useState<string>("WAREHOUSE");
  const [adding, setAdding] = useState(false);
  const [bulk, setBulk] = useState<{ empno: string; label: string }[] | null>(null);

  const owners = useMemo(() => (ownerRows ?? []).filter((o) => o.active), [ownerRows]);
  const dueBy = useMemo(() => {
    const ivl = new Map(owners.map((o) => [o.owner_empno, o.interval_days]));
    const m = new Map<string, number>();
    for (const r of lists ?? []) {
      if (countDue(r, ivl.get(r.owner_empno) ?? 7)) m.set(r.owner_empno, (m.get(r.owner_empno) ?? 0) + 1);
    }
    return m;
  }, [lists, owners]);

  const current = owners.find((o) => o.owner_empno === owner) ?? null;
  const rows = useMemo(() => (lists ?? []).filter((r) => r.owner_empno === owner), [lists, owner]);

  return (
    <section className="rounded-2xl border border-hairline bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="h-5 w-5 text-brand-600" />
        <h2 className="text-[17px] font-bold text-slate-900">Cycle Counts</h2>
        <InfoTip label="How cycle counts work">
          One list per truck, plus the warehouse shelf. Pegasus keeps each person's rotation
          (weekly, every 2 weeks, or monthly — set it on their tab), nudges their phone when
          something's due, and saves every count forever. People and lists carry season to
          season — there's nothing to re-set-up next year. Warehouse counts set shelf on-hand;
          a person's counts track their truck.
        </InfoTip>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <button type="button" onClick={() => { setOwner("WAREHOUSE"); setBulk(null); }}
          className={cn(
            "rounded-full px-3 py-1.5 text-[13px] font-bold",
            owner === "WAREHOUSE" && !bulk ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
          )}>
          Warehouse
        </button>
        {owners.map((o) => {
          const due = dueBy.get(o.owner_empno) ?? 0;
          const on = owner === o.owner_empno && !bulk;
          return (
            <button key={o.owner_empno} type="button" onClick={() => { setOwner(o.owner_empno); setBulk(null); }}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-bold",
                on ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
              )}>
              {o.owner_label}
              {due > 0 && (
                <span className={cn("rounded-full px-1.5 text-[11px] font-extrabold tabular-nums", on ? "bg-white/20" : "bg-amber-200 text-amber-900")}>
                  {due}
                </span>
              )}
            </button>
          );
        })}
        {!adding && (
          <button type="button" onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[13px] font-bold text-brand-700 hover:bg-brand-50">
            <Plus className="h-3.5 w-3.5" strokeWidth={3} /> People
          </button>
        )}
      </div>

      {adding && (
        <AddPeoplePanel
          owners={owners}
          lists={lists ?? []}
          onClose={() => setAdding(false)}
          onSeeded={(first) => { setAdding(false); setBulk(null); setOwner(first); }}
          onBulk={(ps) => { setAdding(false); setBulk(ps); setOwner(ps[0]?.empno ?? "WAREHOUSE"); }}
        />
      )}

      {bulk && bulk.length > 0 ? (
        <div className="mt-3 rounded-xl border border-hairline bg-slate-50 p-3">
          <p className="text-[13px] font-bold text-slate-700">
            Adding to {bulk.length === 1 ? bulk[0].label : `${bulk.length}`}:{" "}
            {bulk.length > 1 && <span className="font-semibold text-slate-500">{bulk.map((p) => p.label).join(", ")}</span>}
          </p>
          <div className="mt-2">
            <PricebookPicker
              placeholder="Search the price book…"
              pickLabel={bulk.length === 1 ? "Add" : "Add to All"}
              autoFocus
              onPick={(it) => seed.mutate(bulk.map((p) => ({
                owner_empno: p.empno,
                owner_label: p.label,
                item_id: it.id,
                part_number: it.part_number,
                description: it.description ?? it.part_number,
              })))}
            />
          </div>
          <button type="button" onClick={() => setBulk(null)}
            className="mt-2 rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-slate-500 hover:bg-slate-100">
            Done
          </button>
        </div>
      ) : owner === "WAREHOUSE"
        ? <CycleCountCard embedded />
        : current && (
          <TruckPanel
            owner={current}
            rows={rows}
            onRemoved={() => setOwner("WAREHOUSE")}
          />
        )}
    </section>
  );
}

/** Phone Today card: shows only when something on MY list is due. */
export function TruckCountCard({ empno }: { empno: string | null }) {
  const { data: rows } = useCountLists(empno ?? undefined, !!empno);
  const { data: ownerRows } = useCountOwners();
  const save = useSaveTruckCounts();
  const [vals, setVals] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  const mine = (ownerRows ?? []).find((o) => o.owner_empno === empno);
  const interval = mine?.interval_days ?? 7;
  const due = useMemo(
    () => (rows ?? []).filter((r) => countDue(r, interval)).slice(0, 5),
    [rows, interval],
  );
  if (!empno || mine?.active === false || (due.length === 0 && !saved)) return null;

  const parsed = (r: CountListRow): number | null => {
    const raw = (vals[r.id] ?? "").trim();
    if (raw === "") return null;
    const n = Number(raw);
    return Number.isNaN(n) || n < 0 ? null : n;
  };
  const filled = due.filter((r) => parsed(r) != null);

  const saveAll = async () => {
    const batch = filled.map((r) => ({ row: r, counted: parsed(r) as number }));
    if (batch.length === 0) return;
    await save.mutateAsync(batch);
    setVals({});
    setSaved(true);
    window.setTimeout(() => setSaved(false), 3000);
  };

  if (due.length === 0) {
    return (
      <section className="mt-3 rounded-3xl bg-white p-4 shadow-sm ring-1 ring-black/5">
        <p className="flex items-center gap-1.5 text-[14px] font-bold text-emerald-700">
          <Check className="h-4 w-4" strokeWidth={3} /> Truck counted — thanks!
        </p>
      </section>
    );
  }

  return (
    <section className="mt-3 rounded-3xl bg-white p-4 shadow-sm ring-1 ring-black/5">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="h-5 w-5" style={{ color: "var(--m-accent, #0f172a)" }} />
        <h3 className="text-[16px] font-extrabold tracking-tight">Count Your Truck</h3>
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[12px] font-extrabold text-amber-800">{due.length}</span>
      </div>
      <ul className="mt-2.5 space-y-2">
        {due.map((r) => (
          <li key={r.id} className="flex items-center gap-3 rounded-2xl border border-black/[0.06] bg-white p-3">
            <div className="min-w-0 flex-1">
              <p className="text-[14.5px] font-semibold leading-snug">{r.description ?? r.part_number}</p>
              <p className="mt-0.5 text-[12px] text-slate-500">
                {r.last_counted_at ? <>had {r.last_qty != null ? qtyText(Number(r.last_qty)) : "?"}</> : "first count"}
              </p>
            </div>
            <input
              value={vals[r.id] ?? ""}
              onChange={(e) => setVals((v) => ({ ...v, [r.id]: e.target.value }))}
              inputMode="decimal"
              placeholder="#"
              aria-label={`Count for ${r.part_number}`}
              className="w-16 shrink-0 rounded-xl border border-black/10 py-2.5 text-center text-[17px] font-extrabold tabular-nums focus:outline-none"
            />
          </li>
        ))}
      </ul>
      <button type="button" disabled={filled.length === 0 || save.isPending} onClick={() => void saveAll()}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 text-[15px] font-bold text-white disabled:opacity-40">
        {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
        {filled.length > 0 ? `Save ${filled.length}` : "Save"}
      </button>
    </section>
  );
}
